import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';

const VoiceAssistantTab = ({ user }) => {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceQuery, setVoiceQuery] = useState('');
  const [voiceConversation, setVoiceConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voiceSessionId] = useState(`voice_${user.id}_${Date.now()}`);

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-IN'; utterance.rate = 0.9; utterance.pitch = 1;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  const stopSpeaking = () => { if ('speechSynthesis' in window) { window.speechSynthesis.cancel(); setIsSpeaking(false); } };

  const handleVoiceQuery = async (query) => {
    if (!query.trim()) return;
    setVoiceConversation(prev => [...prev, { type: 'user', text: query, timestamp: new Date().toISOString() }]);
    setLoading(true); setVoiceQuery('');
    try {
      const response = await axios.post(`${API}/voice/query`, { text_query: query, session_id: voiceSessionId, include_audio_response: false });
      setVoiceConversation(prev => [...prev, { type: 'assistant', text: response.data.response, timestamp: new Date().toISOString() }]);
      speak(response.data.response);
    } catch (error) {
      toast.error('Failed to process your query');
      setVoiceConversation(prev => [...prev, { type: 'assistant', text: 'Sorry, I encountered an error. Please try again.', timestamp: new Date().toISOString() }]);
    } finally { setLoading(false); }
  };

  const startListening = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = false; recognition.interimResults = false; recognition.lang = 'en-IN';
      recognition.onresult = (event) => { const transcript = event.results[0][0].transcript; setVoiceQuery(transcript); handleVoiceQuery(transcript); };
      recognition.onerror = (event) => { toast.error(`Voice error: ${event.error}`); setIsListening(false); };
      recognition.onend = () => setIsListening(false);
      try { recognition.start(); setIsListening(true); toast.info('Listening... Speak now'); }
      catch (error) { toast.error('Could not start voice recognition'); }
    } else { toast.error('Voice recognition not supported'); }
  };

  return (
    <Card className="p-6" data-testid="voice-assistant-tab">
      <h2 className="text-2xl font-bold text-indigo-600 mb-6">Voice Health Assistant</h2>
      <div className="bg-gray-50 rounded-lg p-4 mb-4 h-64 overflow-y-auto">
        {voiceConversation.length === 0 ? (
          <div className="text-center text-gray-500 mt-8"><p className="text-lg mb-2">Talk to your AI Health Assistant</p><p className="text-sm">Click the microphone or type your question</p></div>
        ) : (
          <div className="space-y-3">
            {voiceConversation.map((msg, idx) => (
              <div key={`voice-${idx}`} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg p-3 ${msg.type === 'user' ? 'bg-indigo-600 text-white' : 'bg-white border'}`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button onClick={isListening ? () => setIsListening(false) : startListening} variant={isListening ? 'destructive' : 'default'} className={isListening ? '' : 'bg-indigo-600 hover:bg-indigo-700'} data-testid="voice-mic-btn">
          {isListening ? 'Stop' : 'Speak'}
        </Button>
        {isSpeaking && <Button onClick={stopSpeaking} variant="outline">Stop Speaking</Button>}
        <form onSubmit={(e) => { e.preventDefault(); if (voiceQuery.trim()) handleVoiceQuery(voiceQuery); }} className="flex-1 flex gap-2">
          <input value={voiceQuery} onChange={(e) => setVoiceQuery(e.target.value)} placeholder="Or type your question..." className="flex-1 px-4 py-2 border rounded-lg" disabled={loading} />
          <Button type="submit" disabled={loading || !voiceQuery.trim()} className="bg-indigo-600 hover:bg-indigo-700">Send</Button>
        </form>
      </div>
    </Card>
  );
};

export default VoiceAssistantTab;
